import { useState, type FormEvent } from 'react'
import type { ClientsAdminApi } from '../adminApi.js'
import type { AdminClientSummary } from '../index.js'
import { AUTHORIZATION_SERVER_ISSUER } from '../../core/index.js'
import { defaultKid } from './onboardClient.js'
import { downloadPrivateKey, generatePartnerKey, signProofOfPossession, type PartnerKeyPair } from '../../partner/index.js'
import { Download, KeyRound } from 'lucide-react'
import { Button } from '../../core/index.js'

/** The admin calls this screen uses. */
type AdminApi = ClientsAdminApi

const KID_SUFFIX_BYTES = 2

/**
 * A key ID that will not collide with the one issued at onboarding, which
 * uses the same organization-environment-month stem: a kid must be unique
 * across every client.
 */
function suggestKid(client: AdminClientSummary): string {
  const stem = defaultKid(client.organizationName, client.environment === 'production' ? 'production' : 'sandbox')
  const suffix = [...crypto.getRandomValues(new Uint8Array(KID_SUFFIX_BYTES))].map((byte) => byte.toString(16).padStart(2, '0')).join('')
  return stem === '' ? '' : `${stem}-${suffix}`
}

interface AddedKey {
  key: PartnerKeyPair
  activeKids: string[]
}

function AddedKeyNotice({ added, onDone }: { added: AddedKey; onDone: () => void }) {
  const [hasDownloaded, setHasDownloaded] = useState(false)

  return (
    <div className="border border-green bg-green-lt p-4">
      <p className="text-xs text-ink mb-2">
        Key <span className="font-mono font-bold">{added.key.kid}</span> is active. Download the private key now: it exists only in this page and cannot
        be recovered once you leave or close this notice.
      </p>
      <p className="text-[11px] text-ink-mid mb-3">
        Active keys for this client: <span className="font-mono">{added.activeKids.join(', ')}</span>
      </p>
      <div className="flex items-center gap-4">
        <Button
          icon={Download}
          size="sm"
          onClick={() => {
            downloadPrivateKey(added.key)
            setHasDownloaded(true)
          }}
        >
          {hasDownloaded ? 'Download again' : 'Download private key'}
        </Button>
        <button type="button" onClick={onDone} disabled={!hasDownloaded} className="text-xs font-bold text-ink-mid hover:text-ink disabled:opacity-40">
          Done
        </button>
      </div>
    </div>
  )
}

/**
 * Adds another signing key to an already-onboarded client - for a rotation,
 * or when the original key file has been lost. Runs the same three steps as
 * onboarding (generate in this browser, register the public half, prove
 * possession), and offers the private half as a one-time download. Existing
 * keys stay active.
 */
export function ClientKeys({ api, client }: { api: AdminApi; client: AdminClientSummary }) {
  const [isAdding, setIsAdding] = useState(false)
  const [kid, setKid] = useState('')
  const [isWorking, setIsWorking] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [added, setAdded] = useState<AddedKey | null>(null)

  const inputId = `add-key-${client.registrationId}`
  const trimmedKid = kid.trim()

  function open(): void {
    setKid(suggestKid(client))
    setError(null)
    setIsAdding(true)
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault()
    if (trimmedKid === '' || isWorking) {
      return
    }
    setIsWorking(true)
    setError(null)
    try {
      const key = await generatePartnerKey(trimmedKid)
      const { challenge } = await api.registerKey(client.registrationId, key.publicJwk)
      const proof = await signProofOfPossession(key, client.registrationId, challenge, AUTHORIZATION_SERVER_ISSUER)
      const activated = await api.submitProof(client.registrationId, proof)
      setAdded({ key, activeKids: activated.activeKids })
      setIsAdding(false)
    } catch (err) {
      const reason = err instanceof Error && err.message !== '' ? err.message : 'The key could not be added'
      setError(`${reason}. No usable key was created; try again with a different key ID.`)
    } finally {
      setIsWorking(false)
    }
  }

  if (added) {
    return <AddedKeyNotice added={added} onDone={() => setAdded(null)} />
  }

  if (!isAdding) {
    return (
      <div className="text-xs text-ink-mid flex items-center gap-3">
        <span>Private keys are never stored here, so a lost key file is replaced by adding a new key.</span>
        <button type="button" onClick={open} className="font-bold text-marine hover:text-marine-dk whitespace-nowrap">
          Add a key…
        </button>
      </div>
    )
  }

  return (
    <form onSubmit={(event) => void handleSubmit(event)} className="border border-marine/15 bg-white p-4">
      <label htmlFor={inputId} className="block text-[11px] font-bold text-ink-mid mb-1">
        Key ID (kid)
      </label>
      <input
        id={inputId}
        className="w-full max-w-sm border border-marine/25 px-2.5 py-1.5 text-xs font-mono text-ink focus:outline-none focus:border-marine"
        value={kid}
        onChange={(event) => setKid(event.target.value)}
        autoComplete="off"
        autoFocus
      />
      <p className="text-[11px] text-ink-mid mt-1">Must be unique across all clients. The client's existing keys stay active.</p>
      {error && (
        <p role="alert" className="text-[11px] font-bold text-coral mt-2">
          {error}
        </p>
      )}
      <div className="flex items-center gap-3 mt-3">
        <Button type="submit" icon={KeyRound} size="sm" disabled={trimmedKid === '' || isWorking}>
          {isWorking ? 'Adding key…' : 'Generate and add key'}
        </Button>
        <button type="button" onClick={() => setIsAdding(false)} disabled={isWorking} className="text-xs font-bold text-ink-mid hover:text-ink">
          Cancel
        </button>
      </div>
    </form>
  )
}
